'use client';

import { useMemo, type ComponentType } from 'react';
import { useTranslations } from 'next-intl';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { Box } from '@terra/ui-styled-system';
import type { ReportsReturnDto } from '@terra/shared-contracts-reports';
import {
  ReportActionsCell,
  ReportCreatedAtCell,
  ReportFilesCell,
  ReportFindingsCell,
  ReportSourceCell,
  ReportTitleCell,
  ReportUpdatedAtCell,
  getReportFilesCount,
  getReportFindingsCount,
} from './ReportsTableCells';

type ReportsDataColumn = {
  colId: 'title' | 'source' | 'findings' | 'files' | 'createdAt' | 'updatedAt';
  header: string;
  width?: number;
  flex?: number;
  minWidth: number;
  initialSort?: 'desc';
  valueGetter: (report: ReportsReturnDto) => string | number;
  Cell: ComponentType<{ report: ReportsReturnDto }>;
};

const ACTIONS_COLUMN_WIDTH = 48;

const toTime = (date: Date | string) => new Date(date).getTime();

const renderReportCell =
  ({ Cell }: { Cell: ComponentType<{ report: ReportsReturnDto }> }) =>
  (params: ICellRendererParams<ReportsReturnDto>) =>
    params.data ? (
      <Box h="full" display="flex" alignItems="center" minW={0}>
        <Cell report={params.data} />
      </Box>
    ) : null;

export function useReportsTableColumnDefs({
  onDeleteReport,
}: {
  onDeleteReport: (report: ReportsReturnDto) => void;
}): ColDef<ReportsReturnDto>[] {
  const t = useTranslations('proxy.reports.table');

  return useMemo(() => {
    const columns: ReportsDataColumn[] = [
      {
        colId: 'title',
        header: t('title'),
        flex: 1,
        minWidth: 240,
        valueGetter: (report) => report.title.toLowerCase(),
        Cell: ReportTitleCell,
      },
      {
        colId: 'source',
        header: t('source'),
        width: 140,
        minWidth: 110,
        valueGetter: (report) => report.source,
        Cell: ReportSourceCell,
      },
      {
        colId: 'findings',
        header: t('findings'),
        width: 110,
        minWidth: 90,
        valueGetter: getReportFindingsCount,
        Cell: ReportFindingsCell,
      },
      {
        colId: 'files',
        header: t('files'),
        width: 100,
        minWidth: 80,
        valueGetter: getReportFilesCount,
        Cell: ReportFilesCell,
      },
      {
        colId: 'createdAt',
        header: t('created'),
        width: 180,
        minWidth: 140,
        initialSort: 'desc',
        valueGetter: (report) => toTime(report.createdAt),
        Cell: ReportCreatedAtCell,
      },
      {
        colId: 'updatedAt',
        header: t('updated'),
        width: 180,
        minWidth: 140,
        valueGetter: (report) => toTime(report.updatedAt),
        Cell: ReportUpdatedAtCell,
      },
    ];

    return [
      ...columns.map<ColDef<ReportsReturnDto>>((column) => ({
        colId: column.colId,
        headerName: column.header,
        width: column.width,
        flex: column.flex,
        minWidth: column.minWidth,
        sortable: true,
        initialSort: column.initialSort,
        valueGetter: (params) =>
          params.data ? column.valueGetter(params.data) : '',
        cellRenderer: renderReportCell({ Cell: column.Cell }),
      })),
      {
        colId: 'actions',
        headerName: '',
        width: ACTIONS_COLUMN_WIDTH,
        minWidth: ACTIONS_COLUMN_WIDTH,
        maxWidth: ACTIONS_COLUMN_WIDTH,
        pinned: 'right',
        resizable: false,
        sortable: false,
        suppressMovable: true,
        suppressNavigable: true,
        cellRenderer: (params: ICellRendererParams<ReportsReturnDto>) =>
          params.data ? (
            <Box
              h="full"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <ReportActionsCell
                report={params.data}
                onDelete={onDeleteReport}
              />
            </Box>
          ) : null,
      },
    ];
  }, [onDeleteReport, t]);
}
